import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";
import { Plus, BookOpen, Trash2, Pencil, Loader2, X } from "lucide-react";

const PALETTE = ["#4f46e5", "#0ea5e9", "#f59e0b", "#10b981", "#ef4444", "#8b5cf6", "#ec4899", "#0d9488"];

export default function AdminSubjects() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | subject
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [color, setColor] = useState(PALETTE[0]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/subjects").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing("new"); setName(""); setDesc(""); setColor(PALETTE[0]); setErr("");
  };
  const openEdit = (s) => {
    setEditing(s); setName(s.name); setDesc(s.description || ""); setColor(s.color || PALETTE[0]); setErr("");
  };
  const close = () => setEditing(null);

  const save = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const payload = { name, description: desc, color };
      if (editing === "new") await api.post("/subjects", payload);
      else await api.patch(`/subjects/${editing.id}`, payload);
      close();
      load();
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this subject?")) return;
    await api.delete(`/subjects/${id}`);
    load();
  };

  return (
    <AppShell title="Subjects" subtitle="Curate the taxonomy used by exams, materials and question bank.">
      <div className="flex justify-end mb-4">
        <button onClick={openNew} className="btn-primary text-sm" data-testid="new-subject-btn">
          <Plus className="w-4 h-4" /> New subject
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="subject-list">
        {items.map((s) => (
          <div key={s.id} className="qs-card p-5" data-testid={`subject-card-${s.id}`}>
            <div className="flex items-start justify-between">
              <div className="w-11 h-11 rounded-xl grid place-items-center text-white" style={{ background: s.color || "#4f46e5" }}>
                <BookOpen className="w-5 h-5" />
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => openEdit(s)} className="text-slate-500 hover:text-indigo-600" data-testid={`edit-subject-${s.id}`}>
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => del(s.id)} className="text-rose-500" data-testid={`delete-subject-${s.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            <h3 className="mt-4 font-display text-lg font-bold text-slate-900">{s.name}</h3>
            <p className="mt-2 text-sm text-slate-600 line-clamp-2">{s.description || "No description"}</p>
          </div>
        ))}
        {items.length === 0 && (
          <p className="col-span-full text-center text-slate-500 py-12" data-testid="no-subjects">No subjects yet — add the first one.</p>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="subject-modal">
          <form onSubmit={save} className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-bold text-slate-900">
                {editing === "new" ? "New subject" : "Edit subject"}
              </h3>
              <button type="button" onClick={close} className="text-slate-500 hover:text-slate-800" data-testid="subject-modal-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <label className="block text-xs uppercase tracking-widest font-bold text-slate-500 mb-1">Name</label>
            <input
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
              data-testid="subject-name-input"
            />
            <label className="block mt-3 text-xs uppercase tracking-widest font-bold text-slate-500 mb-1">Description</label>
            <textarea
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              rows={3}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
              data-testid="subject-desc-input"
            />
            <label className="block mt-3 text-xs uppercase tracking-widest font-bold text-slate-500 mb-1">Accent color</label>
            <div className="flex flex-wrap gap-2" data-testid="subject-color-palette">
              {PALETTE.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className={`w-8 h-8 rounded-lg border-2 transition ${color === c ? "border-slate-900 scale-110" : "border-transparent"}`}
                  style={{ background: c }}
                  data-testid={`color-${c}`}
                />
              ))}
            </div>
            {err && <p className="mt-3 text-sm text-rose-600" data-testid="subject-error">{err}</p>}
            <button disabled={busy} className="btn-primary text-sm mt-5 w-full justify-center" data-testid="subject-save-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              {editing === "new" ? "Create" : "Save"}
            </button>
          </form>
        </div>
      )}
    </AppShell>
  );
}
